import {
  Avatar,
  Center,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  HStack,
  IconButton,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import { emptyPaginationResponse } from '@duke-hq/lib/pagination';
import { getUserFullName } from '@duke-hq/lib';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { Plural, Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ArrowRightFromLine } from 'lucide-react';
import { useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { JourneyStatusBadge } from '@duke-hq/ui/components/PlacementStatusBadge/PlacementStatusBadge';
import { trpcAdmin } from '../lib/trpcAdmin';
import {
  PlacementStatusFilter,
  PlacementOnHoldFilter,
  usePlacementStatusFilter,
} from './components/PlacementStatusFilter';
import { ProviderFilter, useProviderFilter } from './components/ProviderFilter';
import { CourseFilter, useCourseFilter } from './components/CourseFilter';
import { ContractFilter, useContractFilter } from './components/ContractFilter';
import { FilterBar } from './components/FilterBar';
import { StudentDrawer } from './StudentDrawer/StudentDrawer';

export function Students(): React.JSX.Element {
  const { pageSize, currentPage, setPage } = usePagination();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enrolmentId: selectedEnrolmentIdParam } = useParams<{ enrolmentId: string }>();
  const selectedEnrolmentId =
    selectedEnrolmentIdParam == null ? null : Number(selectedEnrolmentIdParam);
  const querySearch = (searchParams.get('search') ?? '').trim();
  const { statuses, onHold } = usePlacementStatusFilter();
  const providerIds = useProviderFilter();
  const courseIds = useCourseFilter();
  const contractTypes = useContractFilter();
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();

  const closeDrawer = useCallback(() => {
    const query = searchParams.toString();
    navigate(`/placements/students${query === '' ? '' : `?${query}`}`);
  }, [navigate, searchParams]);

  const { data, isFetching } = trpcAdmin.workPlacements.placements.listJourneys.useQuery(
    {
      search: querySearch === '' ? undefined : querySearch,
      filters:
        statuses.length > 0 ||
        onHold ||
        providerIds.length > 0 ||
        courseIds.length > 0 ||
        contractTypes.length > 0
          ? { statuses, onHold, providerIds, courseIds, contractTypes }
          : undefined,
      pagination: { page: currentPage, limit: pageSize },
    },
    {
      initialData: emptyPaginationResponse(),
      placeholderData: (previousData) => previousData,
    },
  );

  return (
    <>
      {selectedEnrolmentId != null && !Number.isNaN(selectedEnrolmentId) ? (
        <Drawer isOpen onClose={closeDrawer} size={{ base: 'full', lg: 'xl' }}>
          <DrawerOverlay />
          <DrawerContent>
            <StudentDrawer enrolmentId={selectedEnrolmentId} onClose={closeDrawer} />
          </DrawerContent>
        </Drawer>
      ) : null}
      <TableViewLayout
        pageHeader={[
          !isExpanded && (
            <IconButton
              aria-label={t`expand secondary sidebar`}
              key="toggle-expand-sub-sidebar"
              variant="tertiary"
              onClick={toggleExpandSubSidebar}
              size="md"
              icon={<ArrowRightFromLine />}
            />
          ),
          <PageHeading key="page-heading">
            <Trans>Students</Trans>
          </PageHeading>,
        ]}
        filters={
          <>
            <SearchInput maxW="search-input" />
            <FilterBar>
              <ProviderFilter />
              <CourseFilter />
              <ContractFilter />
              <PlacementStatusFilter />
              <PlacementOnHoldFilter />
            </FilterBar>
          </>
        }
        tableActionsLeft={
          <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
            <Plural
              value={data.pagination.totalCount || 0}
              zero="0 students"
              one="1 student"
              other="# students"
            />
          </Text>
        }
        table={
          <>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>
                    <Trans>Student</Trans>
                  </Th>
                  <Th>
                    <Trans>Status</Trans>
                  </Th>
                  <Th>
                    <Trans>Course</Trans>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.items.map((row) => (
                  <Tr
                    key={row.enrolmentId}
                    cursor="pointer"
                    _hover={{ bg: 'bg.subtle' }}
                    onClick={() => {
                      const query = searchParams.toString();
                      navigate(
                        `/placements/students/${row.enrolmentId}${query === '' ? '' : `?${query}`}`,
                      );
                    }}
                  >
                    <Td>
                      <HStack gap={3}>
                        <Avatar
                          size="sm"
                          name={getUserFullName(row.student)}
                          src={row.student.picture_url ?? undefined}
                        />
                        <Stack gap={0}>
                          <Text>{getUserFullName(row.student)}</Text>
                          {row.student.email != null ? (
                            <Text fontSize="xs" color="fg.subtle">
                              {row.student.email}
                            </Text>
                          ) : null}
                        </Stack>
                      </HStack>
                    </Td>
                    <Td>
                      {row.status != null ? (
                        <JourneyStatusBadge status={row.status} onHold={row.onHold} />
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td>{row.course?.title ?? '—'}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {isFetching ? (
              <Center p={8}>
                <Spinner size="xl" />
              </Center>
            ) : data.items.length === 0 ? (
              <Center p={8}>
                <Text textAlign="center">
                  <Trans>No students with placements yet</Trans>
                </Text>
              </Center>
            ) : null}
          </>
        }
        pagination={
          data.items.length > 0 ? (
            <Pagination pagination={data.pagination} onPageChange={setPage} />
          ) : null
        }
      />
    </>
  );
}
